/**
 * SupportDialogs — bundles the three modals used by the Support Center:
 *   • NewChatDialog
 *   • TransferDialog
 *   • ConfirmDialog (archive / delete)
 * Extracted from Support.js during the iter-238 refactor (Feb 2026).
 */
import React from 'react';
import {
  ArrowLeftRight, Archive, Trash2, CheckCircle2, User,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';

export function NewChatDialog({
  open, onOpenChange, language, t,
  newChatPhone, setNewChatPhone,
  newChatName, setNewChatName,
  startNewConversation,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="text-white">{language === 'ar' ? 'محادثة جديدة' : 'New Chat'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm text-[#71717A] mb-2 block">{language === 'ar' ? 'رقم الهاتف (مع رمز الدولة)' : 'Phone Number (with country code)'}</label>
            <Input
              value={newChatPhone}
              onChange={(e) => setNewChatPhone(e.target.value)}
              placeholder="971501234567"
              className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
              dir="ltr"
              data-testid="new-chat-phone"
            />
          </div>
          <div>
            <label className="text-sm text-[#808080] mb-2 block">{t('اسم العميل (اختياري)', 'Customer name (optional)')}</label>
            <Input
              value={newChatName}
              onChange={(e) => setNewChatName(e.target.value)}
              placeholder={t('عميل جديد', 'New customer')}
              className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
              data-testid="new-chat-name"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#2a2a4e] text-white">
            {t('إلغاء', 'Cancel')}
          </Button>
          <Button onClick={startNewConversation} className="bg-[#25D366] hover:bg-[#128C7E] text-white">
            {t('إنشاء محادثة', 'Create Chat')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TransferDialog({
  open, onOpenChange, t,
  admins, currentUser,
  transferTo, setTransferTo,
  transferConversation,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1a1a2e] border-[#2a2a4e] text-white">
        <DialogHeader>
          <DialogTitle className="text-white">{t('تحويل المحادثة', 'Transfer Conversation')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <p className="text-sm text-[#808080]">{t('اختر المشرف لتحويل المحادثة إليه:', 'Pick the admin to transfer this conversation to:')}</p>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {admins.filter(a => a.email !== currentUser.email).map((admin) => (
              <button
                key={admin.email}
                onClick={() => setTransferTo(admin.email)}
                className={`w-full p-3 rounded-xl flex items-center gap-3 transition-colors ${
                  transferTo === admin.email
                    ? 'bg-[#25D366]/20 border border-[#25D366]'
                    : 'bg-[#0a0a0a] border border-[#2a2a4e] hover:border-[#25D366]/50'
                }`}
              >
                <div className="w-10 h-10 rounded-full bg-[#2a2a4e] flex items-center justify-center">
                  <User className="h-5 w-5 text-[#808080]" />
                </div>
                <div className="text-right">
                  <p className="font-medium text-white">{admin.name}</p>
                  <p className="text-xs text-[#808080]">{admin.email}</p>
                </div>
                {transferTo === admin.email && (
                  <CheckCircle2 className="h-5 w-5 text-[#25D366] ms-auto" />
                )}
              </button>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#2a2a4e] text-white">
            {t('إلغاء', 'Cancel')}
          </Button>
          <Button
            onClick={transferConversation}
            disabled={!transferTo}
            className="bg-[#25D366] hover:bg-[#128C7E] text-white"
          >
            <ArrowLeftRight className="h-4 w-4 me-2" />
            {t('تحويل', 'Transfer')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ConfirmDialog({
  confirmDialog, setConfirmDialog, t,
  archiveConversation, deleteConversation,
}) {
  return (
    <Dialog open={!!confirmDialog} onOpenChange={(o) => { if (!o) setConfirmDialog(null); }}>
      <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-md">
        {confirmDialog?.type === 'archive' && (
          <>
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Archive className="h-5 w-5 text-indigo-300" />
                {t('أرشفة المحادثة', 'Archive conversation')}
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-[#A1A1AA] py-3">
              {t(
                `ستُخفى محادثة "${confirmDialog?.name}" من القائمة النشطة. الرسائل تُحفظ ويمكن استعادتها لاحقاً.`,
                `Conversation "${confirmDialog?.name}" will be hidden from the active list. Messages remain stored and can be restored later.`,
              )}
            </p>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setConfirmDialog(null)} className="border-[#2a2a4e] text-white">
                {t('إلغاء', 'Cancel')}
              </Button>
              <Button
                onClick={() => archiveConversation(confirmDialog.phone)}
                className="bg-indigo-500 hover:bg-indigo-600 text-white gap-2"
                data-testid="confirm-archive-btn"
              >
                <Archive className="h-4 w-4" />
                {t('أرشف', 'Archive')}
              </Button>
            </DialogFooter>
          </>
        )}
        {confirmDialog?.type === 'delete' && (
          <>
            <DialogHeader>
              <DialogTitle className="text-red-300 flex items-center gap-2">
                <Trash2 className="h-5 w-5" />
                {t('حذف المحادثة نهائياً', 'Delete conversation permanently')}
              </DialogTitle>
            </DialogHeader>
            <div className="py-3 space-y-2">
              <p className="text-sm text-[#A1A1AA]">
                {t(
                  `سيتم حذف محادثة "${confirmDialog?.name}" وجميع رسائلها نهائياً. لا يمكن التراجع.`,
                  `Conversation "${confirmDialog?.name}" and every message will be permanently deleted. This cannot be undone.`,
                )}
              </p>
              <p className="text-xs text-red-300/80">
                {t('اكتب "حذف" للتأكيد.', 'Type "DELETE" to confirm.')}
              </p>
              <Input
                onChange={(e) => setConfirmDialog(c => c ? { ...c, confirmText: e.target.value } : c)}
                className="bg-black/40 border-red-500/40 text-white"
                data-testid="delete-confirm-input"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setConfirmDialog(null)} className="border-[#2a2a4e] text-white">
                {t('إلغاء', 'Cancel')}
              </Button>
              <Button
                onClick={() => deleteConversation(confirmDialog.phone)}
                disabled={confirmDialog?.confirmText !== t('حذف', 'DELETE')}
                className="bg-red-500 hover:bg-red-600 text-white gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="confirm-delete-btn"
              >
                <Trash2 className="h-4 w-4" />
                {t('احذف نهائياً', 'Delete permanently')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
